import { useUser, SignUpButton } from "@clerk/clerk-react";
import { ArrowRight, Check, Minus } from "lucide-react";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Hairline,
  SEO,
} from "../ui";
import { canonicalFor } from "../../routes";

export interface CompareOutcomeStat {
  label: string;          // e.g. "Time to first live card"
  whobrew: string;        // e.g. "60 seconds"
  competitor: string;     // e.g. "Demo required"
  context?: string;       // e.g. "Sign up, claim handle, ship card."
}

export interface CompareConfig {
  competitor: string;        // "Popl"
  competitorUrl?: string;    // "https://popl.co"
  path: string;
  seoTitle: string;
  seoDescription: string;
  /** One-paragraph framing of who they are and who you are. Legacy — used as
   *  fallback subhead when wedge is absent. */
  positioning: string;
  /** The single sharp wedge for this competitor. Headline is the contrast,
   *  subhead is the substance paragraph that explains why it matters. */
  wedge: {
    headline: string;
    italicWord?: string;
    subhead: string;
  };
  /** Three outcome stats above the chart pair. Per-competitor — V1CE replaces
   *  yearOneCost with a "where the product lives" stat instead of pricing. */
  outcomeStats: [CompareOutcomeStat, CompareOutcomeStat, CompareOutcomeStat];
  /** Comparison rows. Stays — the matrix moves below the fold. */
  rows: { feature: string; whobrew: boolean | string; competitor: boolean | string }[];
  /** Honest section — what they do better. Optional but recommended. */
  whereTheyWin?: string;
  /** V1CE-shape section — "What they do that we could learn from." Roadmap input. */
  learnFromThem?: {
    intro: string;
    items: { title: string; body: string }[];
  };
  /** Bottom-line summary. */
  recommendation: string;
}

// ─── Universal chart data ───────────────────────────────────────────────────
//
// Both charts compare WhoBrew against all four competitors at once — the
// landscape view, identical on every /compare/* page. WhoBrew's bar is
// terracotta accent throughout. Update these constants when the underlying
// claims change; quarterly review per file 09 footnote.

const TIME_TO_FIRST_CARD = [
  { name: "WhoBrew", value: "60s",   barWidth: 20,  caption: "Sign up, claim handle, ship card." },
  { name: "Blinq",   value: "~5 min", barWidth: 80,  caption: "Sign up + customize." },
  { name: "Linq",    value: "~5 min", barWidth: 80,  caption: "Sign up + customize." },
  { name: "Popl",    value: "Days",   barWidth: 500, caption: "Demo required." },
  { name: "V1CE",    value: "Days",   barWidth: 450, caption: "Hardware ships first." },
] as const;

// Six features counted: custom contact form fields, per-link click analytics,
// lead capture with attribution, multi-card management, CSV export, email
// signature builder.
const INCLUSION_FEATURES = [
  "Custom contact form fields",
  "Per-link click analytics",
  "Lead capture with attribution",
  "Multi-card management",
  "CSV export",
  "Email signature builder",
] as const;

const INCLUSION_DATA = [
  { name: "WhoBrew", included: 6, paid: 0, missing: 0, label: "6 of 6 included" },
  { name: "Blinq",   included: 4, paid: 1, missing: 1, label: "4 included" },
  { name: "Linq",    included: 4, paid: 0, missing: 2, label: "4 included" },
  { name: "Popl",    included: 5, paid: 1, missing: 0, label: "5 included on paid tier" },
  { name: "V1CE",    included: 2, paid: 2, missing: 2, label: "2 included" },
] as const;

export function Compare(c: CompareConfig) {
  const { isSignedIn } = useUser();

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "WhoBrew", item: "https://whobrew.us/" },
      { "@type": "ListItem", position: 2, name: "Compare", item: "https://whobrew.us/compare/whobrew-vs-popl" },
      { "@type": "ListItem", position: 3, name: `vs ${c.competitor}`, item: canonicalFor(c.path) },
    ],
  };

  return (
    <>
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={canonicalFor(c.path)}
        jsonLd={breadcrumbJsonLd}
      />

      {/* Hero — wedge headline + substance paragraph. Inter throughout
          (no Fraunces) per file 09 v2. */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container width="prose">
          <Eyebrow>WhoBrew vs {c.competitor}</Eyebrow>
          <h1 className="mt-5 font-sans font-bold text-coffee-900 dark:text-cream-50 text-4xl sm:text-5xl lg:text-[56px] leading-[1.04] tracking-[-0.02em]">
            {c.wedge.italicWord
              ? renderWithItalic(c.wedge.headline, c.wedge.italicWord)
              : c.wedge.headline}
            <span className="text-accent-500">.</span>
          </h1>
          <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 leading-relaxed">
            {c.wedge.subhead}
          </p>
        </Container>
      </Section>

      {/* Three outcome stats — the numbers that compress the comparison. */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            {c.outcomeStats.map((stat, i) => (
              <div
                key={i}
                className={`p-6 sm:p-8 bg-cream-50 dark:bg-coffee-900 ${
                  i > 0 ? "border-t md:border-t-0 md:border-l border-latte-300 dark:border-latte-800" : ""
                }`}
              >
                <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-3">
                  {stat.label}
                </p>
                <p className="font-sans font-bold text-coffee-900 dark:text-cream-50 text-3xl sm:text-4xl tracking-tight tabular-nums">
                  {stat.whobrew}
                </p>
                {stat.context && (
                  <p className="mt-2 text-sm text-coffee-700 dark:text-cream-200">
                    {stat.context}
                  </p>
                )}
                <div className="mt-4 pt-4 border-t border-latte-300 dark:border-latte-800">
                  <p className="text-xs text-coffee-600 dark:text-cream-200">
                    <span className="font-mono uppercase tracking-eyebrow text-coffee-500 dark:text-latte-300 mr-2">
                      {c.competitor}:
                    </span>
                    {stat.competitor}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Time-to-first-card chart — sets the orientation. */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-8">
            <Eyebrow>The landscape · time to first live card</Eyebrow>
            <h2 className="mt-4 font-sans font-bold text-coffee-900 dark:text-cream-50 text-2xl sm:text-3xl tracking-[-0.02em]">
              From sign-up to a card you can hand someone, in seconds<span className="text-accent-500">.</span>
            </h2>
          </div>
          <TimeToFirstCardChart />
          <p className="mt-6 text-sm text-coffee-700 dark:text-cream-200 max-w-2xl leading-relaxed">
            Sign up, claim a handle, ship a live card you can hand to someone — 60 seconds on
            WhoBrew. Blinq and Linq are competitive here. Popl gates first-card on a sales
            demo; V1CE gates it on a card shipping to your address.
          </p>
        </Container>
      </Section>

      {/* What's-included chart — carries the substance. */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-8">
            <Eyebrow>The landscape · feature inclusion</Eyebrow>
            <h2 className="mt-4 font-sans font-bold text-coffee-900 dark:text-cream-50 text-2xl sm:text-3xl tracking-[-0.02em]">
              What you actually pay for, once the add-ons are counted<span className="text-accent-500">.</span>
            </h2>
          </div>
          <InclusionChart />
          <p className="mt-6 text-sm text-coffee-700 dark:text-cream-200 max-w-2xl leading-relaxed">
            The six features counted: {INCLUSION_FEATURES.join(" · ")}. Every claim is
            independently verifiable on each company's pricing or product page; the
            terracotta block is what's included on WhoBrew Pro.
          </p>
        </Container>
      </Section>

      {/* Comparison matrix — supporting evidence below the fold. */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="max-w-3xl mb-8">
            <Eyebrow>The full matrix</Eyebrow>
            <h2 className="mt-4 font-sans font-bold text-coffee-900 dark:text-cream-50 text-2xl sm:text-3xl tracking-[-0.02em]">
              Feature by feature<span className="text-accent-500">.</span>
            </h2>
          </div>
          <div className="border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
            <div className="grid grid-cols-3 bg-cream-100 dark:bg-coffee-800">
              <div className="p-5"><Eyebrow>Feature</Eyebrow></div>
              <div className="p-5 border-l border-latte-300 dark:border-latte-700"><Eyebrow>WhoBrew</Eyebrow></div>
              <div className="p-5 border-l border-latte-300 dark:border-latte-700"><Eyebrow>{c.competitor}</Eyebrow></div>
            </div>
            {c.rows.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-3 border-t border-latte-300 dark:border-latte-800 bg-cream-50 dark:bg-coffee-900"
              >
                <div className="p-5 text-body text-coffee-900 dark:text-cream-50 font-medium">
                  {row.feature}
                </div>
                <div className="p-5 border-l border-latte-300 dark:border-latte-800 text-body-sm text-coffee-700 dark:text-cream-200">
                  <CompareCell value={row.whobrew} accent />
                </div>
                <div className="p-5 border-l border-latte-300 dark:border-latte-800 text-body-sm text-coffee-700 dark:text-cream-200">
                  <CompareCell value={row.competitor} />
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Where they win — honest concession. */}
      {c.whereTheyWin && (
        <Section tone="raised" spacing="default" hairlineBottom>
          <Container width="prose">
            <Eyebrow>Where {c.competitor} wins</Eyebrow>
            <h2 className="mt-4 mb-6 font-sans font-bold text-coffee-900 dark:text-cream-50 text-2xl sm:text-3xl tracking-[-0.02em]">
              The honest read<span className="text-accent-500">.</span>
            </h2>
            <p className="text-body text-coffee-700 dark:text-cream-200 leading-relaxed">
              {c.whereTheyWin}
            </p>
          </Container>
        </Section>
      )}

      {/* What they do that we could learn from — V1CE-shape section. */}
      {c.learnFromThem && (
        <Section tone="page" spacing="default" hairlineBottom>
          <Container width="prose">
            <Eyebrow>What they do that we could learn from</Eyebrow>
            <h2 className="mt-4 mb-6 font-sans font-bold text-coffee-900 dark:text-cream-50 text-2xl sm:text-3xl tracking-[-0.02em]">
              Where {c.competitor} is genuinely ahead<span className="text-accent-500">.</span>
            </h2>
            <p className="text-body text-coffee-700 dark:text-cream-200 leading-relaxed mb-6">
              {c.learnFromThem.intro}
            </p>
            <ul className="space-y-4">
              {c.learnFromThem.items.map((item, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-mono text-coffee-400 dark:text-latte-400 flex-shrink-0">·</span>
                  <div>
                    <p className="font-medium text-coffee-900 dark:text-cream-50">{item.title}</p>
                    <p className="mt-1 text-body-sm text-coffee-700 dark:text-cream-200 leading-relaxed">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Container>
        </Section>
      )}

      {/* Recommendation. */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Bottom line</Eyebrow>
          <h2 className="mt-4 mb-6 font-sans font-bold text-coffee-900 dark:text-cream-50 text-2xl sm:text-3xl tracking-[-0.02em]">
            Who should pick what<span className="text-accent-500">.</span>
          </h2>
          <p className="text-body text-coffee-700 dark:text-cream-200 leading-relaxed">
            {c.recommendation}
          </p>
        </Container>
      </Section>

      <Hairline />

      {/* CTA. */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl font-sans font-bold text-cream-50 dark:text-coffee-900 text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em]">
            Try WhoBrew free<span className="text-accent-500">.</span>
          </h2>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            {isSignedIn ? (
              <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                Open my card
              </Button>
            ) : (
              <SignUpButton mode="modal">
                <Button size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
                  Claim my handle
                </Button>
              </SignUpButton>
            )}
            <Button to="/pricing" variant="secondary" size="lg" onInk>See pricing</Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

// ─── Charts (inline SVG; brand-colored; universal data) ─────────────────────

function TimeToFirstCardChart() {
  const max = 720;
  const labelCol = 100;
  const rowH = 40;
  const barH = 16;
  const top = 20;
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${max} ${top + TIME_TO_FIRST_CARD.length * rowH + 8}`}
        className="w-full h-auto min-w-[640px]"
        role="img"
        aria-label="Time to first live card across WhoBrew, Blinq, Linq, Popl, and V1CE"
      >
        {TIME_TO_FIRST_CARD.map((p, i) => {
          const y = top + i * rowH + 8;
          const isWb = p.name === "WhoBrew";
          return (
            <g key={p.name}>
              <text
                x={labelCol - 10}
                y={y + barH - 3}
                fontFamily="Inter, sans-serif"
                fontSize="13"
                fontWeight="600"
                textAnchor="end"
                fill={isWb ? "#b8521a" : "#1f1410"}
              >
                {p.name}
              </text>
              <rect
                x={labelCol}
                y={y}
                width={p.barWidth}
                height={barH}
                fill={isWb ? "#b8521a" : "#1f1410"}
                opacity={isWb ? 1 : 0.85}
              />
              <text
                x={labelCol + p.barWidth + 10}
                y={y + barH - 3}
                fontFamily="Inter, sans-serif"
                fontSize="12"
                fontWeight={isWb ? 600 : 400}
                fill={isWb ? "#b8521a" : "#1f1410"}
              >
                {p.value}
                {p.caption ? ` · ${p.caption}` : ""}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function InclusionChart() {
  const max = 720;
  const labelCol = 100;
  const rowH = 50;
  const barH = 22;
  const top = 20;
  // Total feature width = 540 (allowing 90 per feature out of 6).
  const segW = 540 / 6;
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${max} ${top + INCLUSION_DATA.length * rowH + 8}`}
        className="w-full h-auto min-w-[640px]"
        role="img"
        aria-label="Feature inclusion stacked bars across WhoBrew, Blinq, Linq, Popl, and V1CE"
      >
        {INCLUSION_DATA.map((p, i) => {
          const y = top + i * rowH + 8;
          const isWb = p.name === "WhoBrew";
          const incW = p.included * segW;
          const paidW = p.paid * segW;
          const missW = p.missing * segW;
          return (
            <g key={p.name}>
              <text
                x={labelCol - 10}
                y={y + barH - 5}
                fontFamily="Inter, sans-serif"
                fontSize="13"
                fontWeight="600"
                textAnchor="end"
                fill={isWb ? "#b8521a" : "#1f1410"}
              >
                {p.name}
              </text>
              {/* Included */}
              {p.included > 0 && (
                <rect x={labelCol} y={y} width={incW} height={barH} fill={isWb ? "#b8521a" : "#1f1410"} />
              )}
              {/* Paid add-on */}
              {p.paid > 0 && (
                <rect x={labelCol + incW} y={y} width={paidW} height={barH} fill="#a8967a" />
              )}
              {/* Missing */}
              {p.missing > 0 && (
                <rect x={labelCol + incW + paidW} y={y} width={missW} height={barH} fill="#dcc6a3" />
              )}
              {/* Inline label */}
              <text
                x={labelCol + 8}
                y={y + barH - 6}
                fontFamily="Inter, sans-serif"
                fontSize="11"
                fontWeight="600"
                fill="#fbf7f1"
              >
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-coffee-700 dark:text-cream-200">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 inline-block bg-accent-500" /> Included on WhoBrew Pro
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 inline-block bg-coffee-900 dark:bg-cream-50" /> Included on competitor's entry paid tier
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 inline-block" style={{ background: "#a8967a" }} /> Paid add-on / upsell
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 inline-block" style={{ background: "#dcc6a3" }} /> Not available
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function CompareCell({ value, accent = false }: { value: boolean | string; accent?: boolean }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-2">
        <Check className={`w-4 h-4 ${accent ? "text-accent-500" : "text-success-700"}`} strokeWidth={2.5} />
        Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-2 text-coffee-600 dark:text-latte-400">
        <Minus className="w-4 h-4" /> No
      </span>
    );
  }
  return <span>{value}</span>;
}

function renderWithItalic(text: string, italicWord: string) {
  const idx = text.toLowerCase().indexOf(italicWord.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const word = text.slice(idx, idx + italicWord.length);
  const after = text.slice(idx + italicWord.length);
  return (
    <>
      {before}
      <em className="italic">{word}</em>
      {after}
    </>
  );
}

export default Compare;
